import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";

const basePath = `${API_URL.idp}/user`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).trim() } : {}),
    ...extra,
  },
});

const getAllTenantUsers = async ({
  size = 20,
  index = 0,
  enabled,
  tenantId,
  ...rest
} = {}) => {
  const params = {
    size,
    index,
    ...rest,
  };

  if (typeof enabled === "boolean") {
    params.enabled = enabled;
  }

  return API.get(
    basePath,
    "tenant_users_list",
    params,
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantUserById = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.get(
    `${basePath}/${userId}`,
    "tenant_user_by_id",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const searchTenantUsers = async (filters = {}) => {
  return getAllTenantUsers({
    size: filters.size ?? 20,
    index: filters.index ?? 0,
    username: filters.username || undefined,
    email: filters.email || undefined,
    mobile: filters.mobile || undefined,
    tenantId: filters.tenantId,
  });
};

const createTenantUser = async (payload = {}, tenantId) => {
  return API.post(
    basePath,
    "tenant_user_create",
    {
      name: String(payload.name || "").trim(),
      username: String(payload.username || "").trim(),
      password: payload.password,
      email: String(payload.email || "").trim(),
      mobile: String(payload.mobile || "").trim(),
      roles: Array.isArray(payload.roles)
        ? payload.roles.filter(Boolean).map(String)
        : [],
      clientId: String(payload.clientId || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUser = async (userId, payload = {}, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  const body = {};
  if (payload.name !== undefined) body.name = String(payload.name || "").trim();
  if (payload.email !== undefined) body.email = String(payload.email || "").trim();
  if (payload.mobile !== undefined) {
    body.mobile = String(payload.mobile || "").trim();
  }
  if (payload.mfaEnabled !== undefined) {
    body.mfaEnabled = Boolean(payload.mfaEnabled);
  }

  return API.put(
    `${basePath}/${userId}`,
    "tenant_user_update",
    body,
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const deleteTenantUser = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.delete(
    `${basePath}/${userId}`,
    "tenant_user_delete",
    { mobile: "" },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserStatus = async (userId, status, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/status`,
    "tenant_user_status",
    {
      status: String(status || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const changeTenantUserPassword = async (userId, newPassword, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/password`,
    "tenant_user_change_password",
    { password: newPassword },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserRole = async (userId, roles, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/role`,
    "tenant_user_update_role",
    { roles },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

export const tenantUserService = {
  getAllTenantUsers,
  getTenantUserById,
  searchTenantUsers,
  createTenantUser,
  updateTenantUser,
  deleteTenantUser,
  updateTenantUserStatus,
  changeTenantUserPassword,
  updateTenantUserRole,
};
